// components/HeroCarousel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, Container, IconButton, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { FaCircle, FaStar } from 'react-icons/fa';
import { useMovies } from '../context/MovieContext';

const HeroCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const { nowPlayingMovies, loadingNowPlaying, error, getImageUrl } = useMovies();
  
  const featuredMovies = nowPlayingMovies.slice(0, 4);

  // Set up and clear interval based on pause state
  useEffect(() => {
    if (featuredMovies.length === 0) return;
    
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setActiveIndex((prevIndex) => 
          prevIndex === featuredMovies.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [featuredMovies.length, isPaused]);

  const handleDotClick = (index) => {
    setActiveIndex(index);
    setIsPaused(true);
  };

  if (loadingNowPlaying) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: {xs: '60vh', md: '80vh'},
        bgcolor: 'background.default'
      }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (error || featuredMovies.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        flexDirection: 'column',
        height: {xs: '60vh', md: '80vh'},
        bgcolor: 'background.default',
        px: 2
      }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error || "No recent movies available to display"}
        </Typography>
        <Button variant="contained" color="secondary" component={Link} to="/now-playing">
          View All Movies
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      height: {xs: '60vh', md: '80vh'}, 
      overflow: 'hidden'
    }}>
      {featuredMovies.map((movie, index) => (
        <Box
          key={movie.id}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(10, 25, 41, 0.9)), url(${getImageUrl(movie.backdrop_path)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: activeIndex === index ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: {xs: '100%', md: '60%'}, color: 'white', p: 2 }}>
              <Typography 
                variant="h1" 
                sx={{
                  fontSize: {xs: '2.5rem', md: '4.5rem'}, 
                  fontWeight: 800,
                  letterSpacing: '-0.5px',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  mb: 2
                }}
              >
                {movie.title}
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4, 
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                  fontSize: {xs: '1rem', md: '1.25rem'},
                  fontWeight: 400,
                }}
              >
                {movie.overview.length > 200 
                  ? `${movie.overview.substring(0, 200)}...` 
                  : movie.overview}
              </Typography>
              <Button 
                component={Link} 
                to={`/movie/${movie.id}`} 
                variant="contained" 
                color="secondary" 
                size="large"
                sx={{ 
                  fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                }}
              >
                Buy Tickets
              </Button>
            </Box>
          </Container>
        </Box>
      ))}

      {/* Pagination dots */}
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: '30px', 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 2
        }}
      >
        {featuredMovies.map((_, index) => (
          <IconButton 
            key={index} 
            onClick={() => handleDotClick(index)}
            size="small"
            sx={{
              p: 0.8,
              color: activeIndex === index ? 'secondary.main' : 'rgba(255, 255, 255, 0.7)',
              transform: activeIndex === index ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease, color 0.3s ease',
            }}
          >
            {activeIndex === index ? 
              <FaStar size={22} /> : 
              <FaCircle size={16} />
            }
          </IconButton>
        ))}
      </Box>
    </Box>
  );
};

export default HeroCarousel;